import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { CartItem, CartState, CartTotals } from "@/types/cart";

const STORAGE_KEY = "paola-saldivia.cart.v1";

type Action =
  | { type: "HYDRATE"; payload: CartState }
  | { type: "ADD"; item: CartItem }
  | { type: "REMOVE"; key: string }
  | { type: "SET_QTY"; key: string; quantity: number }
  | { type: "CLEAR" };

const initial: CartState = { items: [] };

const reducer = (state: CartState, action: Action): CartState => {
  switch (action.type) {
    case "HYDRATE":
      return action.payload;
    case "ADD": {
      const existing = state.items.find((i) => i.key === action.item.key);
      if (existing) {
        const nextQty = existing.quantity + action.item.quantity;
        const capped = action.item.maxStock != null ? Math.min(nextQty, action.item.maxStock) : nextQty;
        return {
          items: state.items.map((i) =>
            i.key === action.item.key ? { ...i, quantity: capped } : i
          ),
        };
      }
      return { items: [...state.items, action.item] };
    }
    case "REMOVE":
      return { items: state.items.filter((i) => i.key !== action.key) };
    case "SET_QTY": {
      if (action.quantity <= 0) return { items: state.items.filter((i) => i.key !== action.key) };
      return {
        items: state.items.map((i) => {
          if (i.key !== action.key) return i;
          const capped = i.maxStock != null ? Math.min(action.quantity, i.maxStock) : action.quantity;
          return { ...i, quantity: capped };
        }),
      };
    }
    case "CLEAR":
      return initial;
    default:
      return state;
  }
};

interface CartContextValue {
  items: CartItem[];
  totals: CartTotals;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  removeItem: (key: string) => void;
  setQuantity: (key: string, quantity: number) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initial);
  const [hydrated, setHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CartState;
        if (parsed && Array.isArray(parsed.items)) {
          dispatch({ type: "HYDRATE", payload: parsed });
        }
      }
    } catch {
      // ignore corrupted storage
    }
    setHydrated(true);
  }, []);

  // Persist on every change after hydration
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota errors
    }
  }, [state, hydrated]);

  const totals: CartTotals = useMemo(() => {
    let subtotal = 0;
    let originalSubtotal = 0;
    let itemsCount = 0;
    for (const i of state.items) {
      subtotal += i.unitPrice * i.quantity;
      originalSubtotal += (i.originalPrice ?? i.unitPrice) * i.quantity;
      itemsCount += i.quantity;
    }
    return {
      itemsCount,
      subtotal,
      originalSubtotal,
      discount: Math.max(0, originalSubtotal - subtotal),
    };
  }, [state.items]);

  const addItem = useCallback<CartContextValue["addItem"]>((item) => {
    const quantity = item.quantity ?? 1;
    const safeImage = item.image && item.image.trim() ? item.image : "/placeholder.svg";
    dispatch({ type: "ADD", item: { ...item, quantity, image: safeImage } });
    toast.success("Adicionado ao carrinho", { description: item.productName });
    setIsOpen(true);
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    // Avisa o usuário se ele tentar passar do estoque disponível
    const item = state.items.find((i) => i.key === key);
    if (item?.maxStock != null && quantity > item.maxStock) {
      toast.warning("Quantidade limitada pelo estoque", {
        description: `Disponível: ${item.maxStock} unidade${item.maxStock === 1 ? "" : "s"}.`,
      });
    }
    dispatch({ type: "SET_QTY", key, quantity });
  }, [state.items]);

  const removeItem = useCallback((key: string) => dispatch({ type: "REMOVE", key }), []);
  const clear = useCallback(() => dispatch({ type: "CLEAR" }), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo<CartContextValue>(
    () => ({ items: state.items, totals, isOpen, openCart, closeCart, toggleCart, addItem, removeItem, setQuantity, clear }),
    [state.items, totals, isOpen, openCart, closeCart, toggleCart, addItem, removeItem, setQuantity, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};

export const formatBRL = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
